import React, { useState } from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import Footer from '../../Footer/Footer';

const dogImage = 'https://BelvoirCastlePull.b-cdn.net/Website/Dog%20Park%201.jpeg';

const getApiBase = () => {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:8888/Website/front-end/public/api';
  } else if (hostname.includes('staging.belvoircastle.com')) {
    return 'https://staging.belvoircastle.com/api';
  }
  return 'https://www.belvoircastle.com/api';
};

const C = {
  navy: '#101d40',
  bronze: '#9d6d49',
  ivory: '#FAF7F0',
  parchment: '#F3EDD8',
  offWhite: '#f8f7f4',
  text: '#5c6370',
};

const Page = styled.div`
  min-height: 100vh;
  padding-top: 120px;
  background-color: ${C.offWhite};
`;

const Hero = styled.div`
  width: 100%;
  height: 420px;
  background-image: linear-gradient(to bottom, rgba(16,29,64,0.35), rgba(16,29,64,0.72)), url(${dogImage});
  background-size: cover;
  background-position: center 30%;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    height: 320px;
  }
`;

const HeroContent = styled.div`
  text-align: center;
  color: white;
  padding: 2rem;
  max-width: 820px;
`;

const HeroBadge = styled.p`
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${C.bronze};
  margin: 0 0 0.75rem;
`;

const HeroTitle = styled.h1`
  font-family: ${props => props.theme.fonts.main};
  font-size: 3.2rem;
  font-weight: 700;
  margin: 0 0 1rem;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  margin: 0;
  opacity: 0.92;
  line-height: 1.6;
  max-width: 600px;
  margin: 0 auto;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const ContentWrapper = styled.div`
  max-width: 960px;
  margin: -60px auto 4rem;
  padding: 0 2rem;
  position: relative;
  z-index: 2;

  @media (max-width: 768px) {
    margin-top: -40px;
    padding: 0 1.2rem;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 14px;
  padding: 3rem;
  box-shadow: 0 10px 40px rgba(16,29,64,0.12);
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

const AccentBar = styled.div`
  width: 48px;
  height: 3px;
  background: ${C.bronze};
  border-radius: 2px;
  margin: 0 auto 1.5rem;
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2rem;
  color: ${C.navy};
  margin: 0 0 0.5rem;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 1.6rem;
  }
`;

const Description = styled.p`
  font-size: 1.05rem;
  line-height: 1.8;
  color: ${C.text};
  text-align: center;
  margin: 0 auto 2.5rem;
  max-width: 620px;
`;

const PerksGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  margin: 0 0 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const PerkBox = styled.div`
  text-align: center;
  padding: 2rem 1.6rem;
  background: ${C.offWhite};
  border-radius: 10px;
  border-top: 3px solid ${C.bronze};

  .perk-number {
    display: block;
    font-family: ${props => props.theme.fonts.main};
    font-size: 2rem;
    font-weight: 700;
    color: ${C.bronze};
    opacity: 0.35;
    margin-bottom: 0.75rem;
    letter-spacing: -0.03em;
  }

  h4 {
    font-family: ${props => props.theme.fonts.main};
    font-size: 1.15rem;
    color: ${C.navy};
    margin: 0 0 0.6rem;
    font-weight: 600;
  }

  p {
    margin: 0;
    font-size: 0.9rem;
    color: ${C.text};
    line-height: 1.65;
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #e8e6e1;
  margin: 2.5rem 0;
`;

const FormTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.6rem;
  color: ${C.navy};
  text-align: center;
  margin: 0 0 0.5rem;
`;

const FormSubtitle = styled.p`
  text-align: center;
  color: ${C.text};
  font-size: 0.95rem;
  margin: 0 0 2rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-width: 580px;
  margin: 0 auto;
`;

const InputRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const Label = styled.label`
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${C.navy};
`;

const inputBase = `
  padding: 0.9rem 1.1rem;
  border: 2px solid #e8e6e1;
  border-radius: 8px;
  font-size: 1rem;
  color: ${C.navy};
  background: white;
  transition: all 0.25s ease;
  width: 100%;
  box-sizing: border-box;

  &::placeholder {
    color: #aaa;
  }

  &:focus {
    outline: none;
    border-color: ${C.bronze};
    box-shadow: 0 0 0 3px ${C.bronze}25;
  }
`;

const Input = styled.input`${inputBase}`;
const Select = styled.select`
  ${inputBase}
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23101d40' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  padding-right: 2.5rem;
`;

const SubmitButton = styled.button`
  background: ${C.navy};
  color: white;
  border: none;
  padding: 1.1rem 2rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 0.5rem;
  font-family: ${props => props.theme.fonts.main};

  &:hover:not(:disabled) {
    background: ${C.bronze};
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(16,29,64,0.2);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const StatusMessage = styled.div`
  text-align: center;
  padding: 1rem 1.5rem;
  border-radius: 8px;
  margin-top: 0.5rem;
  font-weight: 500;
  font-size: 0.95rem;
  line-height: 1.6;
  background: ${props => props.$error ? '#FEE2E2' : '#D1FAE5'};
  color: ${props => props.$error ? '#991B1B' : '#065F46'};
  border: 1px solid ${props => props.$error ? '#FCA5A5' : '#6EE7B7'};
`;

const PrivacyNote = styled.p`
  text-align: center;
  font-size: 0.8rem;
  color: #999;
  margin: 0.75rem 0 0;
  line-height: 1.6;
`;

const HOW_HEARD = [
  'Social media',
  'From a friend or family member',
  'At Belvoir Castle',
  'Email newsletter',
  'Search engine',
  'Local notice / poster',
  'Other',
];

const INITIAL_FORM = {
  firstName: '',
  lastName: '',
  email: '',
  dogName: '',
  dogBreed: '',
  dogAge: '',
  howHeard: '',
};

const DogClubRegister = () => {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [status, setStatus] = useState({ submitting: false, error: null, success: false, message: null });

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ submitting: true, error: null, success: false, message: null });

    try {
      const response = await fetch(`${getApiBase()}/dog-club-register.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok || data?.success === false) {
        throw new Error(data?.error || 'Registration failed. Please try again.');
      }

      setStatus({
        submitting: false,
        error: null,
        success: true,
        message: data?.status === 'already_registered'
          ? `Your interest has already been registered. We will be in touch when the Dog Club launches.`
          : `Thank you for your interest in the Belvoir Dog Club. We will be in touch soon with more details.`,
      });

      if (data?.status !== 'already_registered') {
        setFormData(INITIAL_FORM);
      }
    } catch (err) {
      setStatus({
        submitting: false,
        error: true,
        success: false,
        message: err.message || 'Something went wrong. Please try again or contact us.',
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>Belvoir Dog Club — Register Your Interest | Belvoir Castle</title>
        <meta name="description" content="Register your interest in the Belvoir Castle Dog Club. Be the first to hear when we launch and receive exclusive dog-friendly news, events and offers." />
        <link rel="canonical" href="https://www.belvoircastle.com/dog-club" />
        <meta property="og:title" content="Belvoir Dog Club — Register Your Interest | Belvoir Castle" />
        <meta property="og:description" content="Be among the first to hear when the Belvoir Castle Dog Club launches. Register your interest today." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.belvoircastle.com/dog-club" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <Page>
        <Hero>
          <HeroContent>
            <HeroBadge>Belvoir Castle</HeroBadge>
            <HeroTitle>The Belvoir Dog Club</HeroTitle>
            <HeroSubtitle>
              We're bringing something special to Belvoir for dog owners and their companions.
              Register your interest below to be among the first to hear when we launch.
            </HeroSubtitle>
          </HeroContent>
        </Hero>

        <ContentWrapper>
          <Card>
            <SectionTitle>Coming Soon to Belvoir</SectionTitle>
            <AccentBar />
            <Description>
              Belvoir Castle is proud to be one of the most dog-friendly destinations in the East Midlands.
              We are working on something very exciting for dog owners and their four-legged friends.
              Register your interest now and be the first to know when the Dog Club Membership officially launches.
            </Description>

            <PerksGrid>
              <PerkBox>
                <span className="perk-number">01</span>
                <h4>Off Lead Dog Park</h4>
                <p>A dedicated space on the estate for members and their dogs — a safe off lead park for training & tiring out your four legged friend. Scenic dog walking trails are nearby, too.</p>
              </PerkBox>
              <PerkBox>
                <span className="perk-number">02</span>
                <h4>Belvoir Pet Shop</h4>
                <p>Exclusive member discounts and curated treats, leads, and accessories from the Belvoir estate store.</p>
              </PerkBox>
              <PerkBox>
                <span className="perk-number">03</span>
                <h4>Digital Club House</h4>
                <p>Join our private members' community — connect with fellow dog owners, share updates, and get early access to Club news.</p>
              </PerkBox>
            </PerksGrid>

            <Divider />

            <FormTitle>Register Your Interest</FormTitle>
            <AccentBar />
            <FormSubtitle>Tell us a little about yourself and your dog — we'll be in touch as soon as the Club is ready to welcome you.</FormSubtitle>

            <Form onSubmit={handleSubmit}>
              <InputRow>
                <FieldGroup>
                  <Label htmlFor="firstName">Your First Name *</Label>
                  <Input
                    id="firstName"
                    type="text"
                    name="firstName"
                    placeholder="e.g. Sarah"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                  />
                </FieldGroup>
                <FieldGroup>
                  <Label htmlFor="lastName">Your Last Name *</Label>
                  <Input
                    id="lastName"
                    type="text"
                    name="lastName"
                    placeholder="e.g. Thompson"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                  />
                </FieldGroup>
              </InputRow>

              <FieldGroup>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  name="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </FieldGroup>

              <InputRow>
                <FieldGroup>
                  <Label htmlFor="dogName">Your Dog's Name *</Label>
                  <Input
                    id="dogName"
                    type="text"
                    name="dogName"
                    placeholder="e.g. Biscuit"
                    value={formData.dogName}
                    onChange={handleChange}
                    required
                  />
                </FieldGroup>
                <FieldGroup>
                  <Label htmlFor="dogBreed">Breed *</Label>
                  <Input
                    id="dogBreed"
                    type="text"
                    name="dogBreed"
                    placeholder="e.g. Labrador, Mixed Breed, Cockapoo"
                    value={formData.dogBreed}
                    onChange={handleChange}
                    required
                  />
                </FieldGroup>
              </InputRow>

              <FieldGroup>
                <Label htmlFor="dogAge">How old is your dog? *</Label>
                <Select
                  id="dogAge"
                  name="dogAge"
                  value={formData.dogAge}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select age…</option>
                  <option value="Puppy (under 1 year)">Puppy (under 1 year)</option>
                  <option value="Young (1–3 years)">Young (1–3 years)</option>
                  <option value="Adult (4–7 years)">Adult (4–7 years)</option>
                  <option value="Mature (8–10 years)">Mature (8–10 years)</option>
                  <option value="Senior (11+ years)">Senior (11+ years)</option>
                </Select>
              </FieldGroup>

              <FieldGroup>
                <Label htmlFor="howHeard">How did you hear about the Dog Club?</Label>
                <Select
                  id="howHeard"
                  name="howHeard"
                  value={formData.howHeard}
                  onChange={handleChange}
                >
                  <option value="">Select an option…</option>
                  {HOW_HEARD.map(h => <option key={h} value={h}>{h}</option>)}
                </Select>
              </FieldGroup>

              <SubmitButton type="submit" disabled={status.submitting}>
                {status.submitting ? 'Submitting…' : 'Register Your Interest'}
              </SubmitButton>

              {(status.error || status.success) && (
                <StatusMessage $error={status.error}>
                  {status.message}
                </StatusMessage>
              )}

              <PrivacyNote>
                By submitting this form you agree to receive updates about the Belvoir Dog Club.
                You can unsubscribe at any time. We will never share your details with third parties.
              </PrivacyNote>
            </Form>
          </Card>
        </ContentWrapper>

        <Footer />
      </Page>
    </>
  );
};

export default DogClubRegister;
